import React from 'react';
import { getShopProgress, resetErrors } from '../../modules/shop'
import withRouter from 'with-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import { Link } from 'react-router-dom';
import ItemsList from '../../components/ItemsList'
import EmptyView from '../../components/EmptyView';
import LoadingIndicator from '../../components/LoadingIndicator';
import { Trans, withTranslation, useTranslation } from 'react-i18next';
import { getPageTranslatableResourceSections, SHOP_GROUPS } from '../../config/translatableResourceSections';
import { loadTranslatableResourceSection, saveTranslatableResources, getSectionStateFrom, isSectionLoadedInFull } from '../../modules/translatableResources';
import {
  buildTranslatableFields,
  rowsFromFlatFields,
  buildTranslatableSaveMap,
  translatableProgress
} from '../../utils/translatableResourceFields';

// generic translatable resource types that show up as subsections here
const RESOURCE_SECTIONS = getPageTranslatableResourceSections('shop');

// -1 is ProgressIndicator's "not counted yet" dot. A reported 0 cannot be told apart from a
// section that has no stored row, so it stays unknown until the section itself has been counted
const UNKNOWN_PROGRESS = -1;

const reportedProgress = (props, sectionKey) => {
  const value = props.shop[`${sectionKey}_progress`];
  return typeof value === 'number' && value > 0 ? value : UNKNOWN_PROGRESS;
};

const GROUPED_IDS = SHOP_GROUPS.reduce((all, group) => all.concat(group.ids), []);

// Shopify reports no cookie banner content at all while its own banner is off for every region,
// so an empty section is usually a setting away rather than nothing to translate
const sectionEmptyMessage = (sectionKey, t, shopUrl) => {
  if(sectionKey !== 'cookie_banner') {
    return null;
  }

  const store = (shopUrl || '').replace('.myshopify.com', '');

  return <EmptyView
    title={t('pages.shop.cookieBannerNoContent')}
    description={
      <Trans i18nKey="pages.shop.cookieBannerHint">
        <a
          href={`https://admin.shopify.com/store/${store}/settings/privacy/consent-banner`}
          target="_blank"
          rel="noopener noreferrer"
        ></a>
      </Trans>
    }
    dense
  />;
};

// the list is rendered with hideSorting, so this order is what the user sees
const groupShopData = (items, t) => {
  const ungrouped = items.filter(item => !GROUPED_IDS.includes(item.id));

  return SHOP_GROUPS.reduce((ordered, group, index) => {
    const members = group.ids
      .map(id => items.find(item => item.id === id))
      .filter(Boolean)
      .concat(index === 0 ? ungrouped : []);

    members.forEach(item => ordered.push({
      ...item,
      // groupId draws the card, groupLabel only the heading - the first group is a card
      // without a title
      groupId: group.id,
      groupLabel: group.label ? t(`pages.shop.groups.${group.label}`) : null,
    }));

    return ordered;
  }, []);
};


class Shop extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      data: this.initialData(props),
      errorLockCollapse: false
    }
  }

  // every section unloaded, showing only the indexed progress
  initialData = (props) => groupShopData(RESOURCE_SECTIONS.map(section => ({
    id: section.key,
    progress: reportedProgress(props, section.key),
    title: props.t(`pages.shop.sections.${section.key}`),
    isNew: section.isNew === true,
    synchronized: false,
    emptyMessage: sectionEmptyMessage(section.key, props.t, props.shopUrl),
    fields: []
  })), props.t)

  resourceSectionState = (props, sectionKey) =>
    getSectionStateFrom(props.translatableResources, sectionKey)

  resourceSectionResources = (props, sectionKey) =>
    this.resourceSectionState(props, sectionKey).resources

  isResourceSectionPending = (props, kind) => RESOURCE_SECTIONS.some(section =>
    this.resourceSectionState(props, section.key)[kind] === true)

  // every subsection keeps its own, and each entry already names the section it belongs to
  resourceSectionSaveErrors = (props) => RESOURCE_SECTIONS.reduce((all, section) =>
    all.concat(this.resourceSectionState(props, section.key).saveErrors || []), [])

  // a subsection that has finished loading is one the filter bar can actually search
  hasLoadedSection = () => this.state.data.some(item => item.synchronized === true)
  
  componentDidMount() {
    this.loadAll(this.props.editLanguage)
  }

  loadAll = (language) => {
    // one indexed read, no shopify call - fills the rings before any section has answered
    this.props.getShopProgress(language)

    // every section loads straight away and independently, so the page fills in as each
    // answers rather than waiting on the slowest. it also puts their fields in the page filter
    // without the user having to expand anything
    RESOURCE_SECTIONS
      .slice()
      .sort((a, b) => (a.loadLast === true ? 1 : 0) - (b.loadLast === true ? 1 : 0))
      .forEach(section => {
        // no load more control on this page, so each section has to page itself to the end
        this.props.loadTranslatableResourceSection(section, language, { loadAllPages: true }, true);
      })
  }

  componentWillReceiveProps(newProps) {
    const currentLanguage = this.props.editLanguage;
    const newLanguage = newProps.editLanguage;
    if(currentLanguage != newLanguage) {
      // The rows are accumulated here, not read from the store, so the old language's would
      // stay on screen as loaded until each section answers. Back to the mount state instead.
      this.setState({ data: this.initialData(newProps), errorLockCollapse: false });
      this.loadAll(newLanguage)
      return
    }

    const newData = []

    RESOURCE_SECTIONS.forEach(section => {
      const previous = this.state.data.find(x => x.id === section.key);
      const wasPending = this.resourceSectionState(this.props, section.key);
      const isPending = this.resourceSectionState(newProps, section.key);
      const settled = (wasPending.load_pending === true && isPending.load_pending === false)
        || (wasPending.save_pending === true && isPending.save_pending === false);

      const resources = this.resourceSectionResources(newProps, section.key);
      // a partial recompute would read as authoritative while being too low, so only a
      // fully loaded section may override the reported number
      const loadedInFull = (settled || previous.synchronized)
        && isSectionLoadedInFull(isPending, section);
      newData.push({
        id: section.key,
        progress: loadedInFull
          ? translatableProgress(resources)
          : reportedProgress(newProps, section.key),
        title: this.props.t(`pages.shop.sections.${section.key}`),
        isNew: section.isNew === true,
        synchronized: settled ? true : previous.synchronized,
        emptyMessage: sectionEmptyMessage(section.key, this.props.t, newProps.shopUrl),
        fields: settled
          ? buildTranslatableFields(resources, section)
          : previous.fields
      })
    })

    this.setState({data: groupShopData(newData, this.props.t)});
  }

  onExpand(id) {
    // a second fetch while the mount one is in flight would reset the slice underneath it
    const resourceSection = RESOURCE_SECTIONS.find(x => x.key === id);
    if(resourceSection
      && this.state.data.find(x => x.id === id).synchronized === false
      && this.resourceSectionState(this.props, id).load_pending !== true) {
      this.props.loadTranslatableResourceSection(resourceSection, this.props.editLanguage, { loadAllPages: true }, true);
    }
  }

  onCollapsed(id) {
  }

  onSave = (items, fields) => {
    RESOURCE_SECTIONS.forEach(section => {
      const item = items.find(x => x.id === section.key);
      if(!item) {
        return;
      }
      const resources = this.resourceSectionResources(this.props, section.key);
      const saveMap = buildTranslatableSaveMap(resources, rowsFromFlatFields(resources, item.fields));
      if(Object.keys(saveMap).length > 0) {
        this.props.saveTranslatableResources(section.key, this.props.editLanguage, saveMap);
      }
    })
  }

  render() {
    const { t } = this.props

    return <React.Fragment>
      <Typography variant="h4" gutterBottom component="h2">
        {t('pages.shop.title')}
      </Typography>
      <Divider className="divider-white" />
      <Alert severity="info">
        {t('pages.shop.metafieldsMoved')}
        {' '}
        <Link to="/shop_metafields">{t('navigation.primary.shopMetafields')}</Link>
      </Alert>
      <ItemsList
        type="shop"
        // ItemsList carries its own copy of the rows and, when a load settles, keeps the old
        // row's target for every row it already holds as synchronized - after a language
        // switch that is the previous language's text. A fresh instance holds nothing.
        key={this.props.editLanguage}
        // the subsections are grouped under headings, so their order is fixed and the
        // sort control has to go - it would shuffle rows out of their group
        hideSorting={true}
        // a fixed set of subsections has nothing to page through
        pagination={false}
        // sorts the entries inside a subsection - sorting the subsections themselves is
        // off, they are grouped
        sortFields={true}
        // the raw translation key is the label, and it carries its own meaning
        searchFieldLabels={true}
        // safe here, and only here: this page's overlay is up for saves only, so a section
        // opened mid-load has no other spinner competing with the skeleton's
        showSkeletonSpinner={true}
        // nothing is loaded until a section is expanded, so a search box would only
        // promise otherwise. Loaded, not expanded: collapsing keeps the data
        hideFilterbar={!this.hasLoadedSection()}
        // replaces the bar while hidden, instead of the component's 'Metadata' default
        toolbarTitle={t('pages.shop.filterbarHint')}
        onExpand={(id) => {this.onExpand(id)}}
        onSave={this.onSave}
        // ItemsList syncs its own copy of data on this going false, so it has to stay true
        // for the whole load even though the page no longer waits on it
        isPending={
          this.isResourceSectionPending(this.props, 'load_pending') ||
          this.isResourceSectionPending(this.props, 'save_pending')
        }
        isSaving={this.isResourceSectionPending(this.props, 'save_pending')}
        saveErrors={this.resourceSectionSaveErrors(this.props)}
        data={this.state.data}
        allowAutoTranslation={true}
        allowFieldFiltering={true}
        error={this.props.shop.error}
        errorLockCollapse={this.state.errorLockCollapse}
      />
      <LoadingIndicator
        pending={this.isResourceSectionPending(this.props, 'save_pending')}
        progress={this.props.shop.progress}
        error={this.props.shop.error}
        onErrorCancel={this.props.resetErrors}
        onErrorRetry={() => {
          this.setState({ errorLockCollapse: true }, () => {
            this.setState({ errorLockCollapse: false });
          });
        }}
      />
    </React.Fragment>
  }

}

const mapStateToProps = ({ shop, translatableResources }) => ({
  shop: shop,
  translatableResources: translatableResources,
  editLanguage: shop.shop.edit_language,
  shopUrl: shop.shop.url
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getShopProgress,
      resetErrors,
      loadTranslatableResourceSection,
      saveTranslatableResources
    },
    dispatch
  )

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(Shop)))